import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MagnifyingGlass, X, Plus, Crown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembersDialog({ open, onOpenChange, channel, directory, currentUser, onChanged }) {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState('');
  const canManage = channel && (channel.created_by === currentUser?.id || currentUser?.role === 'admin');

  const load = useCallback(async () => {
    if (!channel) return;
    try {
      const res = await axios.get(`${API}/chat/channels/${channel.id}/members`);
      setMembers(res.data || []);
    } catch (_e) { /* ignore */ }
  }, [channel]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const memberIds = new Set(members.map((m) => m.id));
  const candidates = directory.filter((u) => !memberIds.has(u.id) &&
    (u.name || u.email || '').toLowerCase().includes(q.toLowerCase()));

  const add = async (uid) => {
    try { await axios.post(`${API}/chat/channels/${channel.id}/members`, { user_ids: [uid] }); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || '追加に失敗しました'); }
  };
  const remove = async (uid) => {
    try { await axios.delete(`${API}/chat/channels/${channel.id}/members/${uid}`); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || '削除に失敗しました'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="members-dialog">
        <DialogHeader>
          <DialogTitle>メンバー管理</DialogTitle>
          <DialogDescription className="sr-only">チャンネルのメンバーを追加・削除します。</DialogDescription>
        </DialogHeader>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">メンバー（{members.length}）</p>
          <div className="max-h-44 overflow-y-auto space-y-0.5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-zinc-50" data-testid={`member-row-${m.id}`}>
                <span className="w-7 h-7 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                  {(m.name || m.email || '?').charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0 text-sm text-zinc-800 truncate">{m.name || m.email}</span>
                {channel?.created_by === m.id && <Crown size={13} weight="fill" className="text-amber-500" title="作成者" />}
                {canManage && channel?.created_by !== m.id && (
                  <button type="button" onClick={() => remove(m.id)} data-testid={`remove-member-${m.id}`}
                    className="text-zinc-400 hover:text-red-600"><X size={14} /></button>
                )}
              </div>
            ))}
          </div>
        </div>

        {canManage && (
          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">追加する</p>
            <div className="relative mb-2">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前で検索..." className="pl-9 h-9" data-testid="member-search-input" />
            </div>
            <div className="max-h-40 overflow-y-auto space-y-0.5">
              {candidates.slice(0, 30).map((u) => (
                <button key={u.id} type="button" onClick={() => add(u.id)} data-testid={`add-member-${u.id}`}
                  className="w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-zinc-50">
                  <span className="w-7 h-7 rounded-full bg-zinc-200 text-zinc-600 text-[10px] font-bold flex items-center justify-center shrink-0">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0 text-sm text-zinc-700 truncate">{u.name || u.email}</span>
                  <Plus size={15} className="text-teal-700" />
                </button>
              ))}
              {candidates.length === 0 && <p className="text-xs text-zinc-400 text-center py-3">追加できるユーザーがいません</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
